<template>
  <article class="login-container">
    <section class="main">
      <section class="logo">
        <img class="ifield-logo" src="@/assets/ifield/iFieldlogo.png" />
      </section>
      <login-form
        v-bind:loginCode="form.loginCode"
        v-bind:error-message="apiError"
        class="form"
        v-on:login="onSubmit"
        v-on:forgot-password="$_goToResetPassword"
      />
      <login-information class="information" />
    </section>
    <login-footer />
    <loading-mask is-full-screen v-bind:isLoading="isLoggingIn" />
  </article>
</template>

<script>
import { RestApiClient } from 'ifield-core-components'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { useInitializeAtLoggedIn } from '@/composables/UseInitializeAtLoggedIn.js'
import LoginForm from '@/components/login/AppLoginForm.vue'
import LoginInformation from '@/components/login/AppLoginInformation.vue'
import LoginFooter from '@/components/login/AppLoginFooter.vue'

/**
 * 現在ページのhrefを取得する
 * @returns {string} href
 */
const getCurrentHref = () => {
  const url = new URL(location.href)
  const urlString = url.toString()
  const replaceHolder =
    url.search || (urlString[urlString.length - 1] === '?' ? '?' : '')
  return urlString.replace(replaceHolder, '')
}

/**
 * フォームを使用してログインする画面
 */
export default {
  name: 'TheLoginForm',
  components: {
    LoginForm,
    LoginInformation,
    LoginFooter,
  },
  inheritAttrs: false,
  setup() {
    const { handleApiError } = useHandleApiError()
    const { onAfterLoginSuccess } = useInitializeAtLoggedIn()
    return { handleApiError, onAfterLoginSuccess }
  },
  data: function () {
    return {
      apiError: null,
      form: {
        loginCode: null,
        password: null,
      },
      isLoggingIn: false,
    }
  },
  mounted: function () {
    this.$store.commit('loadLoginCodeFromLocal')
    this.$_getLoginCodeFromStore()
  },
  methods: {
    /**
     * ストアからログインコードを取得する
     */
    $_getLoginCodeFromStore: function () {
      this.form.loginCode = this.$store.state.login.loginCode
    },

    /**
     * submitボタン押下時の処理
     * @param {Object} value フォーム入力値({ loginCode, password })
     */
    onSubmit: async function (value) {
      this.form = { ...value }
      this.$_showLoading()
      this.apiError = null

      try {
        // ログインAPIを実行する
        const login = await this.$_login()
        if (login.status === 'OTP_WAIT') {
          // 多要素認証が未完了
          this.$router.push('/multi-factor-auth')
        } else {
          // storeにログインユーザーの情報を保存する
          this.$store.dispatch('saveLogin', login.data)
          // ログイン後処理
          await this.onAfterLoginSuccess()
          // redirect先が指定されていればそちらのページを起動
          const path = this.$route.query.redirect || '/'
          this.$router.push(path)
        }
      } catch (error) {
        if (error.message) {
          this.apiError = error.message
        } else {
          this.$log.error('unexpected error', error)
          this.apiError = error
        }
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * フォームの情報を使ってログインAPI実行
     */
    $_login: async function () {
      const url = RestApiClient.createApiUrl('login')
      const body = {
        login_code: this.form.loginCode,
        password: this.form.password,
        client_type: 'users_web',
      }
      const option = {
        successStatuses: ['SUCCESS', 'OTP_WAIT'],
      }
      try {
        return await RestApiClient.executePostApi(url, body, option)
      } catch (error) {
        // OPT_WAIT以外のエラーを処理する
        await this.handleApiError(error, this.$_onLoginFailure, true)
      }
    },

    /**
     * ログイン失敗時処理
     * @param {Response|Error} error APIエラー
     */
    $_onLoginFailure: async function (error) {
      let message

      if (error instanceof Response) {
        // 認証に失敗した場合
        const json = await error.json()
        // リダイレクトURLが設定されている場合
        const redirectError =
          json.errors && json.errors.find((error) => error.redirect_url)
        if (redirectError) {
          this.$_redirectToChangePassword(json.message, redirectError)
          message = '' // メッセージ部には何も表示しない
        } else {
          message = json.message || this.$t('theLoginForm.message.serverError')
        }
      } else {
        message = `${this.$t('theLoginForm.message.failedToLogin')}(${error})`
      }

      throw new Error(message)
    },

    /**
     * パスワード変更画面にリダイレクトする
     * @param {String} title タイトルエラーメッセージ
     * @param {Object} error リダイレクトを促しているエラーオブジェクト
     */
    $_redirectToChangePassword: function (title, error) {
      this.$alert
        .fire({
          icon: 'warning',
          title: title,
          text: error.message,
        })
        .then(() => {
          const anchor = document.createElement('a')
          const url = new URL(error.redirect_url)
          url.searchParams.append('back_url', getCurrentHref())
          anchor.href = url.toString()
          anchor.click()
        })
    },

    /**
     * パスワードリセットページへ遷移する
     */
    $_goToResetPassword: function () {
      const anchor = document.createElement('a')
      const url = RestApiClient.createApiUrl(
        'user_password/reset_mail',
        '/ifield/users/api/'
      )
      url.searchParams.append('back_url', getCurrentHref())
      anchor.href = url.toString()
      anchor.click()
    },

    // /**
    //  * OAuth2ログインページへのリダイレクト
    //  */
    // $_redirectToOAuth2: function () {
    //   this.$router.push('/oauth2')
    // },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoggingIn = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoggingIn = false
    },
  },
}
</script>

<style lang="scss" scoped>
.login-container {
  background-color: #fafafa;
  width: 100%;
  height: 100%;
  padding-bottom: 32px;
  display: grid;
  grid-template-rows: 1fr max-content 0;
}

.main {
  flex-shrink: 0;
  width: 100%;
  max-width: 1016px;
  margin: auto;
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-template-rows: 80px 1fr;
  row-gap: 4px;
  column-gap: 64px;
}

.logo {
  grid-column: 1 / 3;
  grid-row: 1;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;

  .ifield-logo {
    height: 80px;
    position: relative;
    left: -18px;
  }
}

.form {
  grid-column: 1;
  grid-row: 2;
}

.information {
  grid-column: 2;
  grid-row: 2;
  margin-top: 48px;
}
</style>
